import React from 'react'
import { LucideIcon } from 'lucide-react'
import { Control, FieldValues, Path, useController } from 'react-hook-form'
import { Form } from 'react-bootstrap'
import styled from 'styled-components'

interface Option {
  key: string
  icon: LucideIcon
  label: string
  props?: React.ButtonHTMLAttributes<HTMLButtonElement>
  tooltip?: string
  onClick?: (arg: string) => void
}

interface RadioButtonSelectProps<T extends FieldValues> {
  control: Control<T>
  name: Path<T>
  options: Option[]
  label?: string
  optional?: boolean
  horizontal?: boolean
}

const RadioInput = styled(Form.Check)`
  input:checked {
    background-color: #2c6b4e;
    border-color: #2c6b4e;
  }
`

export function RadioIconSelect<T extends FieldValues>({
  control,
  name,
  options,
  label,
  optional = false,
}: RadioButtonSelectProps<T>) {
  const {
    field: { onChange, value },
    fieldState: { error },
  } = useController({ name, control })

  return (
    <div className="flex flex-col w-full">
      {label && (
        <label
          htmlFor={name}
          className="text-base mt-3 font-medium text-gray-800 select-none mb-2"
        >
          {label}
          {optional && (
            <span className="text-sm italic font-normal"> - optional</span>
          )}
        </label>
      )}
      <div className="flex flex-col gap-2">
        {options.map((option) => {
          return (
            <div
              key={option.key}
              onClick={() => {
                onChange(option.key)
                option?.onClick?.(option.key)
              }}
              style={{ border: '1px solid' }}
              className={
                value === option.key
                  ? 'rounded-md bg-new-company-pistachio-light border-fb-dark-green text-fb-dark-green'
                  : 'rounded-md border border-gray-400 text-gray-500'
              }
            >
              <div className="flex justify-between p-2">
                <button
                  key={option.key}
                  type="button"
                  className="flex gap-2 items-center"
                  {...option.props}
                >
                  <option.icon className="w-5 h-5" />
                  <span
                    className={`${value === option.key ? 'font-bold' : ''}`}
                  >
                    {option.label}
                  </span>
                </button>
                <RadioInput
                  type="radio"
                  checked={value === option.key}
                  readOnly
                />
              </div>
            </div>
          )
        })}
      </div>
      {error && <p className="text-red-500 text-sm mt-1">{error.message}</p>}
    </div>
  )
}
